import React from 'react'
import { Select, Tabs } from '@/components/ds'
import { QueryState } from '@/components/common'
import { Ic } from '@/components/icons'
import {
  listAllCampaigns,
  listEmailEvents,
  listSendOutcomes,
  listUnsubscribeEvents,
} from '@/lib/db'
import { isArchived } from '@shared/archive'
import { CHART } from '@/lib/dashboard'
import { useAsync } from '@/lib/useAsync'
import type { ScreenProps } from '@/App'

const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
]

interface CampaignRow {
  campaign_id: string
  name: string
  /** Archived campaigns stay here — their history is the reason they were kept. */
  archived: boolean
  sent: number
  failed: number
  opens: number
  clicks: number
  openPct: number
  clickPct: number
  unsubs: number
}

/** Series identity, fixed order — never cycled, never reassigned by rank. */
const SERIES = {
  sent: CHART.indigo,
  opens: CHART.green,
  clicks: CHART.amber,
  bounce: CHART.red,
} as const

const DAY = 24 * 60 * 60 * 1000

/**
 * The home for stats. Everything is aggregated client-side from three capped
 * reads (terminal queue rows, email_events, unsubscribes) — counts are raw
 * event totals, rates are unique per email (distinct queue rows), which is the
 * honest number when one recipient opens five times.
 */
export function AnalyticsScreen({ narrow }: ScreenProps) {
  const [range, setRange] = React.useState('30')
  const [campaignId, setCampaignId] = React.useState('')

  const since =
    range === 'all' ? undefined : new Date(Date.now() - Number(range) * DAY)
  const sinceMs = since?.getTime() ?? 0

  // ARCHIVED INCLUDED, deliberately. Archiving exists so a campaign can leave
  // the Campaigns list without taking its statistics with it — this screen is
  // where those statistics live, so hiding them here would defeat the point.
  const campaigns = useAsync(() => listAllCampaigns(), [])
  const outcomes = useAsync(
    () => listSendOutcomes({ campaignId: campaignId || undefined }),
    [campaignId],
  )
  const events = useAsync(
    () => listEmailEvents({ since, campaignId: campaignId || undefined }),
    [campaignId, range],
  )
  const unsubs = useAsync(
    () => listUnsubscribeEvents({ since, campaignId: campaignId || undefined }),
    [campaignId, range],
  )

  const loading = outcomes.loading || events.loading || unsubs.loading || campaigns.loading
  const error = outcomes.error ?? events.error ?? unsubs.error ?? campaigns.error

  // ---- aggregate ------------------------------------------------------------
  const byCampaign = new Map<
    string,
    { sent: number; failed: number; opens: number; clicks: number; openQ: Set<string>; clickQ: Set<string>; unsubs: number }
  >()
  const bucket = (id: string | null) => {
    const key = id ?? 'none'
    let b = byCampaign.get(key)
    if (!b) {
      b = { sent: 0, failed: 0, opens: 0, clicks: 0, openQ: new Set(), clickQ: new Set(), unsubs: 0 }
      byCampaign.set(key, b)
    }
    return b
  }

  // Daily buckets for the sparklines, plus the previous window for the deltas.
  const spanDays = range === 'all' ? 30 : Number(range)
  const startOfDay = (ms: number) => {
    const d = new Date(ms)
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }
  const today = startOfDay(Date.now())
  const firstDay = today - (spanDays - 1) * DAY
  const blank = () => new Array(spanDays).fill(0) as number[]
  const series = { sent: blank(), opens: blank(), clicks: blank(), bounce: blank() }
  const prev = { sent: 0, opens: 0, clicks: 0, bounce: 0 }
  const prevStart = firstDay - spanDays * DAY

  const put = (key: keyof typeof series, whenMs: number) => {
    const day = startOfDay(whenMs)
    if (day >= firstDay) {
      const i = Math.round((day - firstDay) / DAY)
      if (i >= 0 && i < spanDays) series[key][i]++
    } else if (day >= prevStart) {
      prev[key]++
    }
  }

  for (const o of outcomes.data ?? []) {
    // sent rows date by delivery, failed rows by creation
    const whenMs = new Date(o.status === 'sent' ? (o.sent_at ?? o.created_at) : o.created_at).getTime()
    if (whenMs >= sinceMs) {
      const b = bucket(o.campaign_id)
      if (o.status === 'sent') b.sent++
      else b.failed++
    }
    if (o.status === 'sent') put('sent', whenMs)
    else put('bounce', whenMs)
  }
  for (const e of events.data ?? []) {
    const b = bucket(e.campaign_id)
    if (e.type === 'open') {
      b.opens++
      if (e.queue_id) b.openQ.add(e.queue_id)
    } else {
      b.clicks++
      if (e.queue_id) b.clickQ.add(e.queue_id)
    }
    put(e.type === 'open' ? 'opens' : 'clicks', new Date(e.at).getTime())
  }
  for (const u of unsubs.data ?? []) {
    bucket(u.campaign_id).unsubs++
    put('bounce', new Date(u.at).getTime())
  }

  const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0)
  const fmtPct = (v: number) => `${v.toFixed(1)}%`

  const totals = { sent: 0, failed: 0, opens: 0, clicks: 0, openQ: 0, clickQ: 0, unsubs: 0 }
  for (const b of byCampaign.values()) {
    totals.sent += b.sent
    totals.failed += b.failed
    totals.opens += b.opens
    totals.clicks += b.clicks
    totals.openQ += b.openQ.size
    totals.clickQ += b.clickQ.size
    totals.unsubs += b.unsubs
  }

  const rows: CampaignRow[] = (campaigns.data ?? [])
    .filter((c) => !campaignId || c.id === campaignId)
    .map((c) => {
      const b = byCampaign.get(c.id)
      return {
        campaign_id: c.id,
        name: c.name,
        archived: isArchived(c),
        sent: b?.sent ?? 0,
        failed: b?.failed ?? 0,
        opens: b?.opens ?? 0,
        clicks: b?.clicks ?? 0,
        openPct: pct(b?.openQ.size ?? 0, b?.sent ?? 0),
        clickPct: pct(b?.clickQ.size ?? 0, b?.sent ?? 0),
        unsubs: b?.unsubs ?? 0,
      }
    })
    .sort((a, b) => b.sent - a.sent)

  const bounced = totals.failed + totals.unsubs
  const label = range === 'all' ? 'all time' : `prev ${range}d`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* --- filters, one row above the charts ------------------------------ */}
      <div
        className="lm-card"
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '10px 12px',
        }}
      >
        <Tabs tabs={RANGES} value={range} onChange={setRange} />
        <div style={{ width: narrow ? '100%' : 220 }}>
          <Select
            options={[
              { value: '', label: 'All campaigns' },
              ...(campaigns.data ?? []).map((c) => ({
                value: c.id,
                label: isArchived(c) ? `${c.name} (archived)` : c.name,
              })),
            ]}
            value={campaignId}
            onChange={setCampaignId}
          />
        </div>
      </div>

      <QueryState loading={loading} error={error} onRetry={outcomes.reload}>
        {/* --- KPI tiles ---------------------------------------------------- */}
        <div className="lm-kpis">
          <Kpi
            label="Sent"
            value={totals.sent}
            tint={SERIES.sent}
            data={series.sent}
            prev={prev.sent}
            rangeLabel={label}
            icon={<Ic.send size={13} />}
          />
          <Kpi
            label="Opens"
            value={totals.opens}
            tint={SERIES.opens}
            data={series.opens}
            prev={prev.opens}
            rangeLabel={label}
            icon={<Ic.eye size={13} />}
          />
          <Kpi
            label="Clicks"
            value={totals.clicks}
            tint={SERIES.clicks}
            data={series.clicks}
            prev={prev.clicks}
            rangeLabel={label}
            icon={<Ic.check size={13} />}
          />
          <Kpi
            label="Bounced / unsub"
            value={bounced}
            tint={SERIES.bounce}
            data={series.bounce}
            prev={prev.bounce}
            rangeLabel={label}
            invert
            icon={<Ic.ban size={13} />}
          />
        </div>

        {/* --- rates + funnel ----------------------------------------------- */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: narrow ? 'minmax(0,1fr)' : 'minmax(0, 1.25fr) minmax(0, 1fr)',
            gap: 14,
          }}
        >
          <section className="lm-card">
            <h2 className="lm-card-title">Rates</h2>
            <p className="lm-card-sub" style={{ marginBottom: 14 }}>
              Distinct messages opened / clicked ÷ sent.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 18,
                flexWrap: 'wrap',
                justifyContent: narrow ? 'space-around' : 'flex-start',
              }}
            >
              <Ring
                label="Open rate"
                pct={pct(totals.openQ, totals.sent)}
                part={totals.openQ}
                whole={totals.sent}
                color={SERIES.opens}
              />
              <Ring
                label="Click rate"
                pct={pct(totals.clickQ, totals.sent)}
                part={totals.clickQ}
                whole={totals.sent}
                color={SERIES.clicks}
              />
              <Ring
                label="Bounce"
                pct={pct(bounced, totals.sent)}
                part={bounced}
                whole={totals.sent}
                color={SERIES.bounce}
              />
            </div>
          </section>

          <section className="lm-card">
            <h2 className="lm-card-title">Funnel</h2>
            <p className="lm-card-sub" style={{ marginBottom: 10 }}>
              Where they drop off.
            </p>
            <Funnel sent={totals.sent} opened={totals.openQ} clicked={totals.clickQ} />
          </section>
        </div>

        {/* --- by campaign --------------------------------------------------- */}
        <section className="lm-card">
          <h2 className="lm-card-title">By campaign</h2>
          <p className="lm-card-sub" style={{ marginBottom: 12 }}>
            Open &amp; click rate per campaign — live for real deliveries only.
          </p>
          {rows.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              No campaigns in this range.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {rows.map((r, i) => (
                <CampaignBars key={r.campaign_id} row={r} index={i} fmtPct={fmtPct} />
              ))}
            </div>
          )}
        </section>

        <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
          Opens and clicks are raw event counts; the rates count each email once (distinct messages
          opened or clicked ÷ sent). Events only exist for real deliveries — dry runs and previews
          are never tracked.
        </p>
      </QueryState>
    </div>
  )
}

// -----------------------------------------------------------------------------

/**
 * One KPI. The number is the headline and wears a TEXT token, never the series
 * colour — the tint identifies, the ink communicates. The sparkline is a bare
 * trend (no axes, no dots): it answers "which way" and defers the magnitude to
 * the number above it.
 */
function Kpi({
  label,
  value,
  tint,
  data,
  prev,
  rangeLabel,
  icon,
  invert,
}: {
  label: string
  value: number
  tint: string
  data: number[]
  prev: number
  rangeLabel: string
  icon: React.ReactNode
  /** for bounce, "up" is the bad direction */
  invert?: boolean
}) {
  const delta = prev > 0 ? ((value - prev) / prev) * 100 : null
  const up = (delta ?? 0) >= 0
  const good = invert ? !up : up
  const tone =
    delta === null || Math.abs(delta) < 0.5
      ? 'var(--text-muted)'
      : good
        ? 'var(--status-sent-fg)'
        : 'var(--status-failed-fg)'

  return (
    <div className="lm-tile" style={{ ['--tint' as string]: tint }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span
          className="lm-ico"
          style={{
            width: 22,
            height: 22,
            borderRadius: 7,
            background: `linear-gradient(140deg, ${tint}, ${tint}bb)`,
          }}
        >
          {icon}
        </span>
        <span className="lm-tile-label">{label}</span>
      </div>
      <div className="lm-tile-value tnum">{value.toLocaleString()}</div>
      <div className="lm-tile-delta">
        {delta === null ? (
          <span style={{ color: 'var(--text-faint)' }}>no prior data</span>
        ) : (
          <span style={{ color: tone }}>
            {up ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}% vs {rangeLabel}
          </span>
        )}
      </div>
      <Spark data={data} color={tint} />
    </div>
  )
}

/** A bare trend line. 2px stroke, soft fill, no axes — it is not a chart to read
 *  values off, so it carries none. */
function Spark({ data, color }: { data: number[]; color: string }) {
  const w = 100
  const h = 26
  const max = Math.max(1, ...data)
  const n = data.length
  const pts = data.map((v, i) => {
    const x = n === 1 ? w : (i / (n - 1)) * w
    const y = h - 2 - (v / max) * (h - 5)
    return `${x.toFixed(2)},${y.toFixed(2)}`
  })
  const id = React.useId()

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: 26, display: 'block', marginTop: 'auto' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts.join(' ')} ${w},${h}`} fill={`url(#${id})`} />
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

/**
 * A single-value radial. The percentage sits inside the ring and the raw
 * "x of y" underneath, so the value is legible as text even where the arc's
 * colour is low-contrast on a light surface.
 */
function Ring({
  label,
  pct,
  part,
  whole,
  color,
}: {
  label: string
  pct: number
  part: number
  whole: number
  color: string
}) {
  const size = 76
  const stroke = 8
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const filled = Math.max(0, Math.min(100, pct)) / 100

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
      <svg width={size} height={size} style={{ flex: 'none' }} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--surface-sunken)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${c * filled} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          style={{
            fontSize: 16,
            fontWeight: 650,
            fill: 'var(--text-primary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {Math.round(pct)}%
        </text>
      </svg>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
        <div className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {part.toLocaleString()} of {whole.toLocaleString()}
        </div>
      </div>
    </div>
  )
}

/** Sent → Opened → Clicked as one donut. Three categories, so a legend is
 *  present and each row is direct-labelled with its count. */
function Funnel({ sent, opened, clicked }: { sent: number; opened: number; clicked: number }) {
  // Same three hues the KPI tiles use, so a colour means the same thing
  // everywhere on this screen. Red is deliberately NOT here: it is the reserved
  // bounce/failure status, and painting "Clicked" with it read as a problem.
  const steps = [
    { label: 'Sent', value: sent, color: SERIES.sent },
    { label: 'Opened', value: opened, color: SERIES.opens },
    { label: 'Clicked', value: clicked, color: SERIES.clicks },
  ]
  const total = steps.reduce((a, s) => a + s.value, 0)
  const size = 132
  const stroke = 20
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r

  let offset = 0
  const arcs = steps.map((s) => {
    const frac = total > 0 ? s.value / total : 0
    // 2px of surface between segments, so adjacent fills never touch
    const len = Math.max(0, c * frac - 2)
    const arc = { ...s, dash: `${len} ${c}`, off: -offset }
    offset += c * frac
    return arc
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', flex: 'none' }}>
        <svg width={size} height={size} aria-hidden="true">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-sunken)" strokeWidth={stroke} />
          {total > 0 &&
            arcs.map((a) => (
              <circle
                key={a.label}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={a.color}
                strokeWidth={stroke}
                strokeDasharray={a.dash}
                strokeDashoffset={a.off}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            ))}
          <text
            x="50%"
            y="47%"
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: 22, fontWeight: 650, fill: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}
          >
            {sent.toLocaleString()}
          </text>
          <text
            x="50%"
            y="62%"
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: 10.5, fill: 'var(--text-muted)' }}
          >
            sent
          </text>
        </svg>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
        {steps.map((s) => (
          <li key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span
              style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flex: 'none' }}
              aria-hidden="true"
            />
            <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{s.label}</span>
            <span className="tnum" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              {s.value.toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Colour follows the ENTITY (its campaign id), not its rank — re-sorting or
 *  filtering the list must not repaint the survivors. */
const DOTS = [CHART.indigo, CHART.green, CHART.amber, CHART.pink, CHART.blue, CHART.red]
function dotFor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return DOTS[h % DOTS.length]
}

function CampaignBars({
  row,
  index,
  fmtPct,
}: {
  row: CampaignRow
  index: number
  fmtPct: (v: number) => string
}) {
  const dot = dotFor(row.campaign_id)
  return (
    <div style={{ minWidth: 0, paddingTop: index === 0 ? 0 : 12, borderTop: index === 0 ? 'none' : '1px solid var(--glass-edge)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <span
          style={{ width: 9, height: 9, borderRadius: 3, background: dot, flex: 'none' }}
          aria-hidden="true"
        />
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: 0 }}>
          {row.name}
        </span>
        {/* Archived campaigns are kept here on purpose — their numbers are the
            reason the campaign was archived rather than deleted. */}
        {row.archived && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-faint)',
              background: 'var(--pill-neutral-bg)',
              borderRadius: 4,
              padding: '1px 6px',
              flex: 'none',
            }}
          >
            archived
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {row.sent.toLocaleString()} sent · {row.opens.toLocaleString()} opens ·{' '}
          {row.clicks.toLocaleString()} clicks
          {row.unsubs > 0 && ` · ${row.unsubs} unsub`}
        </span>
      </div>

      <Meter label="Open" pct={row.openPct} shown={row.sent > 0} fmtPct={fmtPct} from={SERIES.opens} to="#5fe0ad" />
      <div style={{ height: 6 }} />
      <Meter label="Click" pct={row.clickPct} shown={row.sent > 0} fmtPct={fmtPct} from={SERIES.clicks} to="#ffd98a" />
    </div>
  )
}

function Meter({
  label,
  pct,
  shown,
  fmtPct,
  from,
  to,
}: {
  label: string
  pct: number
  shown: boolean
  fmtPct: (v: number) => string
  from: string
  to: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--text-faint)',
          width: 38,
          flex: 'none',
        }}
      >
        {label}
      </span>
      <div className="lm-meter" style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            width: `${Math.max(0, Math.min(100, pct))}%`,
            ['--from' as string]: from,
            ['--to' as string]: to,
          }}
        />
      </div>
      <span
        className="tnum"
        style={{ fontSize: 12, color: shown ? 'var(--text-secondary)' : 'var(--text-faint)', width: 46, textAlign: 'right', flex: 'none' }}
      >
        {shown ? fmtPct(pct) : '—'}
      </span>
    </div>
  )
}

